import apiProtected from '@lib/api/apiClient';
import { asList, getBody, server } from '@lib/api/http';
import { queryKeys } from '@lib/api/queryKeys';
import { useSession } from '@lib/auth/session';
import { useMutation, useQuery } from '@tanstack/react-query';

export const useRatingCategoriesQuery = (jobId?: string) => {
  const loggedIn = useSession();
  return useQuery<any>({
    queryKey: queryKeys.ratingCategories(jobId),
    queryFn: async () => {
      const body = await getBody<{ data?: unknown }>(
        `${server}v1/rating-criteria-users/get-rating-criterias-for-job-v2`,
        { jobId }
      );
      return body.data ?? {};
    },
    enabled: loggedIn && Boolean(jobId),
  });
};

export const useRatingOptionsQuery = (optionId?: string | null) => {
  const loggedIn = useSession();
  return useQuery({
    queryKey: queryKeys.ratingOptions(optionId ?? undefined),
    queryFn: async () =>
      asList(
        await getBody<{ data?: unknown[] }>(
          `${server}v1/rating-criteria-users/get-rating-criteria-options`,
          { optionId }
        )
      ),
    enabled: loggedIn && Boolean(optionId),
  });
};

export const useRatingDamagesQuery = (criteriaId?: string, jobId?: string) => {
  const loggedIn = useSession();
  return useQuery({
    queryKey: queryKeys.ratingDamages(criteriaId, jobId),
    queryFn: async () =>
      asList(
        await getBody<{ data?: unknown[] }>(
          `${server}v1/rating-criterias/get-rating-criteria-damages`,
          { id: criteriaId, jobId }
        )
      ),
    enabled: loggedIn && criteriaId != null && Boolean(jobId),
  });
};

export interface RatingVideo {
  id?: string | number;
  url?: string;
}

export const useRatingVideosQuery = (jobId?: string) => {
  const loggedIn = useSession();
  return useQuery({
    queryKey: queryKeys.ratingVideos(jobId),
    queryFn: async () =>
      asList(
        await getBody<{ data?: RatingVideo[] }>(
          `${server}v1/rating-criteria-users/get-rating-videos`,
          { jobId }
        )
      ),
    enabled: loggedIn && Boolean(jobId),
  });
};

export const useRatingSpecialMessageQuery = (jobId?: string) => {
  const loggedIn = useSession();
  return useQuery({
    queryKey: queryKeys.ratingSpecialMessage(jobId),
    queryFn: async () => {
      const body = await getBody<{ data?: { specialMessage?: string | null } }>(
        `${server}v1/rating-special-messages/`,
        { jobId }
      );
      return body.data?.specialMessage?.trim() || null;
    },
    enabled: loggedIn && Boolean(jobId),
  });
};

export const useUploadRatingVideosMutation = () => {
  return useMutation({
    mutationFn: (input: { links: string[]; jobId: string | number }) =>
      apiProtected.post(
        `${server}v1/rating-criteria-users/post-rating-videos`,
        input
      ),
  });
};

export const useSaveRatingCriteriaMutation = () => {
  return useMutation({
    mutationFn: (input: {
      jobId?: string | number;
      ratingCriteriaId?: string | number;
      ratingCriteriaOptionId?: string | number | null;
      damageIds?: Array<string | number>;
      criteriaInfo?: string;
      images?: string[] | undefined;
    }) => apiProtected.post(`${server}v1/rating-criteria-users`, input),
  });
};

export const useRateBuyerMutation = () => {
  return useMutation({
    mutationFn: (input: {
      rating: number;
      ratingType: string;
      appRating: number;
      betterAsACompanyComment: string;
      improvementComment: string;
      jobId: string | number;
    }) => apiProtected.post(`${server}v1/user-ratings`, input),
  });
};

export const useBuyerRatingMutation = () => {
  return useMutation({
    mutationFn: (input: {
      jobId: string;
      appRating: number;
      rating: number;
      betterAsACompanyComment: string;
      improvementComment: string;
    }) =>
      apiProtected
        .post(`${server}v1/user-ratings`, {
          rating: input.rating || 0,
          ratingType: 'r8tr',
          appRating: input.appRating || 0,
          betterAsACompanyComment: input.betterAsACompanyComment,
          improvementComment: input.improvementComment,
          jobId: input.jobId,
        })
        .then((res) => res.data),
  });
};

export const useBuyerPurchaseStatMutation = () => {
  return useMutation({
    mutationFn: (input: {
      jobId: string;
      moneyR8trSavedYou: number;
      r8trHelpedYouDecide: boolean;
      boughtTheProduct: boolean;
    }) =>
      apiProtected
        .post(
          `${server}v1/buyer-purchase-stats/post-buyer-purchase-stat`,
          input
        )
        .then((res) => res.data),
  });
};

export const useSaveSpecialMessageMutation = () => {
  return useMutation({
    mutationFn: (input: {
      jobId: string | number;
      specialMessage: string | null;
      active: boolean;
    }) => apiProtected.post(`${server}v1/rating-special-messages/`, input),
  });
};
